// Client-side face recognition using @vladmandic/face-api (TensorFlow.js).
// Only 128-d descriptors ever leave the browser — raw images are never uploaded.
import * as faceapi from "@vladmandic/face-api";

const MODEL_URL = "https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.15/model";

let loaded = false;
let loading = null;

export async function loadModels() {
  if (loaded) return;
  if (loading) return loading;
  loading = (async () => {
    await faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL);
    await faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL);
    await faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL);
    loaded = true;
  })();
  return loading;
}

const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.5 });

// Returns { descriptor: number[], box, ok } or { ok:false, reason }
export async function detectFace(videoEl) {
  if (!loaded) await loadModels();
  const result = await faceapi
    .detectSingleFace(videoEl, options)
    .withFaceLandmarks()
    .withFaceDescriptor();
  if (!result) return { ok: false, reason: "no_face" };
  // basic liveness / quality heuristic: face must be reasonably large & centered
  const { box } = result.detection;
  const vw = videoEl.videoWidth || 640;
  const areaRatio = (box.width * box.height) / (vw * (videoEl.videoHeight || 480));
  if (areaRatio < 0.05) return { ok: false, reason: "too_far" };
  return { ok: true, descriptor: Array.from(result.descriptor), box, score: result.detection.score };
}

// Capture an averaged descriptor over a few frames for a stronger enrolment template
export async function captureEnrolmentDescriptor(videoEl, samples = 4) {
  if (!loaded) await loadModels();
  const collected = [];
  for (let i = 0; i < samples * 2 && collected.length < samples; i++) {
    const r = await detectFace(videoEl);
    if (r.ok) collected.push(r.descriptor);
    await new Promise((res) => setTimeout(res, 250));
  }
  if (collected.length === 0) return { ok: false, reason: "no_face" };
  const len = collected[0].length;
  const avg = new Array(len).fill(0);
  collected.forEach((d) => d.forEach((v, i) => (avg[i] += v)));
  return { ok: true, descriptor: avg.map((v) => v / collected.length), frames: collected.length };
}
